/* @flow */
import React, { useMemo } from "react";
import Media from "react-media";
import idx from "idx";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Motion, spring } from "react-motion";
import styled, { css } from "styled-components";
import RefreshIcon from "@material-ui/icons/RefreshOutlined";
import FeatureFlags from "../../FeatureFlags";
import { NewsLink, Link as RawLink } from "../common/Link";
import { get } from "../common/fetch";
import Gettext from "../common/Gettext";
import ClickOutside from "../common/ClickOutside";
import TournamentTab from "../Tournaments/Account/Tab";
import Tournament from "../Tournaments/Api";
import { ModalConsumer } from "../common/ModalProvider";
//import _Scrollable from "../common/Scrollable";
import { JoinPopup } from "../Tournaments/Popups";

import Money from "../Money";
import SimpleSpinner from "../Spinners/SimpleSpinner";

import OrderSensitiveAccountTab from "./Tab";
import AccountInfo from "./AccountInfo";
import AccountButton from "./AccountButton";
import TournamentsTabContents from "../Tournaments/Account/TabContents";

import { useCompensations } from "../Protection";
import { getAmountLimit } from "../TradeModals/CompensationPopup";

import type { MapStateToProps } from "react-redux";
import type { Account, Profile } from "../../redux/CommonTypes";
import type { Dispatch } from "../../redux/action";
import type { State as ReduxState } from "../../redux/reducer";
import type { AccountType } from "./AccountInHeader";
import { gaReducer } from "../GA";

import { GiftIcon, AdviceIcon, HotIcon } from "./Icons";
import InviteImg from "../Tournaments/images/invite@3x.png";
import ShieldIcon from "../Protection/ShieldIcon";

import bonusBg from "../../images/compensation-bonus-bg.png";

const SpinnerWrapper = styled.div`
  text-align: center;
  padding: 40px;
`;

const Container = styled.div`
  position: absolute;
  top: 74px;
  right: 0px;
  width: 496px;
  padding: 22px;
  font-weight: 400;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 1;
  color: ${({ theme }) => theme.foreground};
  background-color: ${(props) => props.theme.toggleAccountDarkBg};
  z-index: 10200;

  ${process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
    css`
      @media (min-width: 960px) {
        box-shadow: 0 6px 6px 0 rgba(0, 0, 0, 0.16);
      }
    `};

  @media (min-width: 960px) {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 72px);
    overflow: hidden;
  }

  @media (max-width: 959px) {
    position: relative;
    top: 0;
    flex-grow: 1;
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    background-color: transparent;
    display: flex;
    flex-direction: column;
  }
`;

const Header = styled.h2`
  width: 100%;
  margin: 0 0 13px 0;
  padding: 0 0 18px 0;
  text-align: center;
  text-transform: uppercase;
  border-bottom: 1px solid ${(props) => props.theme.mainSeparator};

  @media (max-width: 959px) {
    display: none;
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  align-content: flex-end;
  width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  border-bottom: 1px solid ${(props) => props.theme.mainSeparator};

  @media (max-width: 959px) {
    border-bottom: none;
  }

  @media (max-width: 479px) {
    padding: 10px;
  }
`;

const AccountView = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 23px 18px 18px;
  /* background-color: ${(props) => props.theme.toggleAccountLightBg}; */
  overflow-x:hidden;
  overflow-y: auto;

  @media (max-width: 959px) {
    flex-grow: 1;
    flex-shrink: 0;
    flex-flow: column nowrap;
    height: 100%;
    max-height: calc(100% - 60px);
    padding: 23px 18px;
    
  }

  @media (max-width: 479px) {
    padding: 10px;
  }
`;

const AccountRow = styled.div`
  width: 100%;
  padding: 0 0 23px;

  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.mainSeparator};
    margin-bottom: 20px;
  }

  :last-child {
    padding-bottom: 0px;
  }

  @media (max-width: 959px) {
    padding-bottom: 20px;

    &:not(:last-child) {
      border-bottom-color: ${(props) => props.theme.borderMainColor};
    }
  }
`;

const AccountRowFlex = styled(AccountRow)`
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;

  /* portrait */
  @media (max-width: 479px) {
    flex-wrap: nowrap;
  }
`;

const BtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: space-between;
  justify-content: space-between;
  align-items: center;
`;

const BtnInner = styled(BtnContainer)`
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: flex-start;
`;

const Link = styled(RawLink)`
  color: ${(props) => props.theme.accent};
  text-decoration: underline;
  transition: all 0.3s ease;

  :hover {
    color: #eb8a00;
  }
`;

const BonusHeader = styled.h2`
  padding-bottom: 18px;
  font-weight: 400;
  font-size: 18px;
  line-height: 1;
  color: ${({ theme }) => theme.foreground};
`;

const BonusSubheader = styled.p`
  direction: ${(props) => props.theme.direction};
  padding-bottom: 4px;
`;

const BalanceView = styled.div`
  padding-right: 12px;
  font-weight: 700;
  line-height: 22px;
  white-space: nowrap;

  ${(props) =>
    props.dark &&
    css`
      padding-left: 12px;
      color: ${(props) => props.theme.mainDark};
    `};
`;

const Currency = styled(Money.Symbol)`
  font-weight: 400;
`;

// prettier-ignore
const ProgressContainer = styled.div`
  display: table;
`;

const ProgressInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  :last-of-type {
    width: 100%;
  }
`;

const ProgressBar = styled.div`
  position: relative;
  display: block;
  width: 100%;
  height: 22px;
  background-color: #99959d;
`;

const ProgressBarText = styled.span`
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const ProgressBarFill = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  background-color: ${(props) => props.theme.accent};
  z-index: 0;
`;

const Mobile = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  /* landscape */
  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: space-between;
    align-content: space-between;
    align-items: center;
  }
`;

const BalanceMobileContainer = styled.div`
  direction: ${(props) => props.theme.direction};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  text-align: center;

  /* portrait */
  @media (max-width: 479px) {
    margin-bottom: 18px;
  }
`;

const BalanceMobile = styled.span`
  margin-left: 6px;
  font-size: 24px;
`;

// const Scrollable = styled(_Scrollable)`
//   margin-bottom: 20px;
//   @media (max-width: 479px) {
//     margin-bottom: 10px;
//   }
// `;

// const ScrollableInner = styled.div`
//   padding-right: 15px;
//   @media (max-width: 479px) {
//     padding-right: 0;
//   }
// `;

type BalanceProps = {
  children: any,
  dark?: boolean,
};

function Balance({ children, dark }: BalanceProps) {
  return (
    <BalanceView dark={dark}>
      <Money Symbol={Currency}>{children}</Money>
    </BalanceView>
  );
}

type Props = {
  style?: Object,
  accounts: Array<Account>,
  profile: Profile,
  history: Object,
  dispatch: Dispatch,
  tournaments: {
    [key: "start" | "final"]: {
      id?: number,
      stats?: any,
    },
  },
  api: {
    fetchEntity: Function,
    readEntity: Function,
  },
  queue: Array<{
    component: React.ComponentType<*>,
    props: any,
  }>,
  showTournamentLeave: boolean,
  showTournamentRefill: boolean,
  showTournamentJoin: boolean,
  showTournamentInfo: boolean,
  onDepositClick: () => void,
  onRefillDemoClick: () => void,
  onProfileUpdate: () => void,
  onClose: () => void,
};

type State = {
  currentTab: "tournament" | "demo" | "real",
  isFetching: boolean,
};

class ToggleAccount extends React.Component<Props, State> {
  activeAccount: void | {
    balance: string,
    id: number,
    isActive: boolean,
    type: "demo" | "real",
  };

  constructor(props) {
    super(props);

    const { accounts, profile } = props;

    const activeAccount = accounts.find(
      (account) => account.id === profile.active_account_id
    );
    const { type = "demo" } = activeAccount;

    this.state = {
      currentTab: props.mode === "tournament" ? "tournament" : type,
      isFetching: false,
      participationFee: null,
      tournamentForDemoMoney: null,
    };
  }

  componentDidMount() {
    const { api } = this.props;

    const announcements = idx(
      api.readEntity("/api/bell-tolls-items"),
      (d) => d.value.payload.data
    );

    const features = idx(api.readEntity("/api/features/list"), (d) => d.value);

    features &&
      features.forEach((item) => {
        if (item.name === "participation_fee") {
          this.setState({
            participationFee: item.info.participation_fee,
          });
        }
      });

    announcements &&
      announcements.forEach((announcement) => {
        if (
          announcement.type === "invited-tournament" &&
          announcement.info["2x_demo_balance_invite"]
        ) {
          this.setState({
            tournamentForDemoMoney: announcement.info,
          });
        }
      });
  }

  onTabClick = (tab: AccountType) => {
    if (tab === this.state.currentTab) {
      return false;
    }

    switch (tab) {
      case "tournament":
        const tournament_account = idx(
          this.props.api.readEntity("/api/tournaments/stats"),
          (stats) => stats.value.active_tournament.account
        );

        if (tournament_account != null) {
          this.props.api.fetchEntity(
            "/api/tournaments/operations?account_id=" + tournament_account.id
          );

          this.props.dispatch({
            type: "ENGAGE_TOURNAMENT_MODE",
          });
        }

        this.setState({
          currentTab: "tournament",
        });
        break;
      case "real":
      case "demo":
      default:
        const account = this.props.accounts.find(
          (account) => account.type === tab
        );

        if (!account) {
          if (tab === "real") {
            this.setState({
              currentTab: "real_stub",
            });
            return;
          }

          return this.props.history.push("/fillup");
        }

        this.setState({ isFetching: true });

        get(`/ajax/setAccount?id=${account.id}`)
          .then((data) => {
            if (data.status === "OK") {
              this.setState({ currentTab: tab });

              if (typeof this.props.onProfileUpdate === "function") {
                this.props
                  .onProfileUpdate()
                  .then(() => this.props.dispatch({ type: "SWITCH_ACCOUNTS" }));
              } else {
                this.props.dispatch({ type: "SWITCH_ACCOUNTS" });
              }
              this.props.api.fetchEntity("/api/compensations/info");
            } else {
              this.props.history.push("/fillup");
            }
          })
          .catch((error) => error)
          .then(() => this.setState({ isFetching: false }));
    }
  };

  render() {
    const {
      accounts,
      onClose,
      style,
      profile,
      onDepositClick,
      onRefillDemoClick,
      refillDemoAvailable,
    } = this.props;

    const activeAccount = accounts.find(
      (account) => account.id === profile.active_account_id
    );

    if (typeof activeAccount !== "object") {
      return null;
    }

    const realAccount = accounts.find((account) => account.type === "real");
    const demoAccount = accounts.find((account) => account.type === "demo");

    const realBalance = realAccount ? realAccount.balance : 0;
    const demoBalance = demoAccount ? demoAccount.balance : 0;

    const holdMenu = this.props.queue.length > 0;

    return (
      <ClickOutside onOutsideClick={onClose} disabled={holdMenu}>
        {({ innerRef }) => (
          <Container ref={innerRef} style={style}>
            <Header>
              <Gettext>Account type</Gettext>
            </Header>

            <TabContainer>
              {FeatureFlags.enableTournaments && (
                <TournamentTab
                  currentTab={this.state.currentTab}
                  onClick={this.onTabClick}
                />
              )}
              <OrderSensitiveAccountTab
                tab="real"
                currentTab={this.state.currentTab}
                onClick={this.onTabClick}
              >
                <Gettext>Real account</Gettext>
              </OrderSensitiveAccountTab>
              <OrderSensitiveAccountTab
                tab="demo"
                currentTab={this.state.currentTab}
                onClick={this.onTabClick}
              >
                <Gettext>Demo account</Gettext>
              </OrderSensitiveAccountTab>
            </TabContainer>

            <TabContentSwitcher
              {...this.state}
              demoBalance={demoBalance}
              realBalance={realBalance}
              profile={profile}
              onClose={onClose}
              onDepositClick={onDepositClick}
              onRefillDemoClick={onRefillDemoClick}
              refillDemoAvailable={refillDemoAvailable}
              showRefillLoader={
                this.props.showRefillLoader || !this.props.profileReady
              }
              showRefillDemo={
                this.props.tradeMode === "binary" &&
                activeAccount.type === "demo"
              }
              participationFee={this.state.participationFee}
              tournamentForDemoMoney={this.state.tournamentForDemoMoney}
              compensationUpdated={this.props.compensationUpdated}
              compensationCreated={this.props.compensationCreated}
              isTradePage
            />
          </Container>
        )}
      </ClickOutside>
    );
  }
}

type ButtonsProps = {
  balance: string | number,
  onClose: () => void,
  onDepositClick: () => void,
  children?: React.Node,
};

function Buttons({
  balance,
  onClose,
  onDepositClick,
  onRefillDemoClick,
  children,
  showTrade = true,
  showRefillDemo,
  refillDemoAvailable,
  showRefillLoader,
  isReal,
}: ButtonsProps) {
  return (
    <AccountRow>
      <Media query="(max-width: 959px)">
        {(matches) =>
          matches ? (
            <Mobile>
              <BalanceMobileContainer>
                <Gettext>Balance</Gettext>:{" "}
                <BalanceMobile>
                  <Money>{balance}</Money>
                </BalanceMobile>
              </BalanceMobileContainer>
              {showRefillLoader && (
                <SpinnerWrapper>
                  <SimpleSpinner accent />
                </SpinnerWrapper>
              )}
              {showRefillDemo && refillDemoAvailable && (
                <AccountButton
                  style={{ marginBottom: 20 }}
                  type="trade"
                  mobile={true}
                  onClick={(e) => {
                    onClose(e);
                    onRefillDemoClick();
                  }}
                >
                  <RefreshIcon style={{ fontSize: 28 }} color="inherit" />
                  <Gettext>Refill</Gettext>
                </AccountButton>
              )}
              {showTrade && (
                <AccountButton
                  type="refill"
                  onClick={(e) => {
                    gaReducer("CLICKBTN_TRADE_DEPOSIT_ACCOUNT", {
                      buttonText: e.currentTarget.textContent,
                      accountType: isReal ? "real" : "demo",
                    });
                    onClose(e);
                  }}
                >
                  <Gettext>Trade</Gettext>
                </AccountButton>
              )}
            </Mobile>
          ) : (
            <BtnContainer>
              <BtnInner>
                <AccountButton
                  type="refill"
                  onClick={(e) => {
                    gaReducer("CLICKBTN_TRADE_DEPOSIT_ACCOUNT", {
                      buttonText: e.currentTarget.textContent,
                      accountType: isReal ? "real" : "demo",
                    });
                    onClose(e);
                    onDepositClick();
                  }}
                >
                  <Gettext>Deposit</Gettext>
                </AccountButton>

                {showRefillLoader && (
                  <SimpleSpinner accent style={{ marginLeft: 12 }} />
                )}
                {showRefillDemo && refillDemoAvailable && (
                  <AccountButton
                    style={{ marginLeft: 12 }}
                    type="trade"
                    onClick={(e) => {
                      onClose(e);
                      onRefillDemoClick();
                    }}
                  >
                    <RefreshIcon style={{ fontSize: 28 }} color="inherit" />
                    <Gettext>Refill</Gettext>
                  </AccountButton>
                )}
                {children}
              </BtnInner>

              {showTrade && (
                <BtnInner>
                  <AccountButton
                    type="trade"
                    onClick={(e) => {
                      gaReducer("CLICKBTN_TRADE_DEPOSIT_ACCOUNT", {
                        buttonText: e.currentTarget.textContent,
                        accountType: isReal ? "real" : "demo",
                      });
                      onClose(e);
                    }}
                  >
                    <Gettext>Trade</Gettext>
                  </AccountButton>
                </BtnInner>
              )}
            </BtnContainer>
          )
        }
      </Media>
    </AccountRow>
  );
}

type TabSwitchProps = {
  demoBalance: string | number,
  realBalance: string | number,
  profile: Profile,
  isFetching: boolean,
  currentTab: AccountType,
  onDepositClick: () => void,
  onClose: () => void,
};

function TabContentSwitcher({
  isFetching,
  currentTab,
  profile,
  onClose,
  onDepositClick,
  onRefillDemoClick,
  refillDemoAvailable,
  showRefillLoader,
  demoBalance,
  realBalance,
  showRefillDemo,
  tournamentForDemoMoney,
  participationFee,
  isTradePage,
  compensationUpdated,
  compensationCreated,
}: TabSwitchProps) {
  if (isFetching === true) {
    return (
      <SpinnerWrapper>
        <SimpleSpinner />
      </SpinnerWrapper>
    );
  }

  switch (currentTab) {
    case "tournament":
      return (
        <TournamentsTabContents isTradePage={isTradePage} onClose={onClose} />
      );
    case "real":
      return (
        <AccountView>
          <Buttons
            onClose={onClose}
            onDepositClick={onDepositClick}
            balance={realBalance}
            isReal={true}
          >
            <AccountButton type="withdraw" to="/withdraw">
              <Gettext>Withdraw</Gettext>
            </AccountButton>
          </Buttons>
          <RealAccountTabContent
            bonus={profile.bonus}
            actions={
              <React.Fragment>
                <AccountButton type="refill" to="/fillup">
                  <Gettext>Deposit</Gettext>
                </AccountButton>

                <AccountButton type="withdraw" to="/withdraw">
                  <Gettext>Withdraw</Gettext>
                </AccountButton>
              </React.Fragment>
            }
          />
        </AccountView>
      );
    case "real_stub":
      return (
        <AccountView>
          <Media query="(max-width: 959px)">
            {(shouldShowTradeButton) => (
              <Buttons
                onClose={onClose}
                onDepositClick={onDepositClick}
                balance={realBalance}
                isReal={true}
                showTrade={shouldShowTradeButton}
              />
            )}
          </Media>
          <AccountRowFlex>
            <AccountInfo>
              <Gettext>
                Trading is available only if you have funds on your real
                account.
              </Gettext>
            </AccountInfo>
          </AccountRowFlex>
          <Media
            query="(max-width: 959px)"
            render={() => (
              <AccountRowFlex>
                <AccountButton
                  type="refill"
                  onClick={(e) => {
                    gaReducer("CLICKBTN_TRADE_DEPOSIT_ACCOUNT", {
                      buttonText: e.currentTarget.textContent,
                      accountType: "real",
                    });
                    onDepositClick();
                    onClose(e);
                  }}
                >
                  <Gettext>Deposit</Gettext>
                </AccountButton>

                <AccountButton type="withdraw" to="/withdraw">
                  <Gettext>Withdraw</Gettext>
                </AccountButton>
              </AccountRowFlex>
            )}
          />
        </AccountView>
      );
    case "demo":
    default:
      return (
        <AccountView>
          <Buttons
            balance={demoBalance}
            onClose={onClose}
            onDepositClick={onDepositClick}
            onRefillDemoClick={onRefillDemoClick}
            refillDemoAvailable={refillDemoAvailable}
            showRefillLoader={showRefillLoader}
            showRefillDemo={showRefillDemo}
          />
          {/* <Scrollable>
            <ScrollableInner> */}
          {(tournamentForDemoMoney &&
            tournamentForDemoMoney.participation_status === 1) ||
          !participationFee ? null : tournamentForDemoMoney ? (
            <Congratulation tournament={tournamentForDemoMoney} type={"1"} />
          ) : participationFee > profile.balance.demo &&
            process.env.REACT_APP_DEPLOY_TARGET !== "eztrader" ? (
            <Advice participationFee={participationFee} />
          ) : null}
          {/* </ScrollableInner>
          </Scrollable> */}
          {profile.real !== 1 && (
            <Bonus
              compensationUpdated={compensationUpdated}
              compensationCreated={compensationCreated}
            />
          )}

          <DemoAccountTabContent
            onClose={onClose}
            onDepositClick={onDepositClick}
          />
        </AccountView>
      );
  }
}

type RealProps = {
  bonus: Object,
  actions: React.Node,
};

function RealAccountTabContent({ bonus, actions }: RealProps) {
  if (!bonus || !bonus.bonus_sum) {
    return (
      <Media query="(max-width: 959px)">
        {(matches) =>
          matches ? <AccountRowFlex>{actions}</AccountRowFlex> : <AccountRow />
        }
      </Media>
    );
  }

  const { bonus_sum: sum, bonus_multiplier: coeff, turnover } = bonus;
  const cashflow = sum * coeff;
  const progressPercent = Math.min(
    100,
    Math.round((turnover * 100) / cashflow)
  );

  return (
    <React.Fragment>
      <AccountRow>
        <BonusHeader>
          <Gettext>You have an active bonus</Gettext>
        </BonusHeader>

        <BonusSubheader>
          <Gettext>Mandatory Trading Volume</Gettext>:
        </BonusSubheader>

        <ProgressContainer>
          <ProgressInner>
            <Balance>{cashflow}</Balance>
          </ProgressInner>

          <ProgressInner>
            <ProgressBar>
              <ProgressBarText>
                <Balance dark>{turnover}</Balance>
              </ProgressBarText>
              <Motion
                defaultStyle={{ x: 0 }}
                style={{ x: spring(progressPercent) }}
              >
                {(value) => (
                  <ProgressBarFill style={{ width: `${value.x}%` }} />
                )}
              </Motion>
            </ProgressBar>
          </ProgressInner>
        </ProgressContainer>
      </AccountRow>
      <AccountRow>
        <AccountInfo>
          <Gettext>
            You need to reach a mandatory trading turnover that displayed on
            this progress bar to withdraw your bonus. When it is completely
            full, you can withdraw your bonus funds.
          </Gettext>
          <br />
          <br />
          <Link to="/bonus-program">
            <Gettext>The Bonus program Policy</Gettext>
          </Link>
        </AccountInfo>
      </AccountRow>

      <Media
        query="(max-width: 959px)"
        render={() => <AccountRowFlex>{actions}</AccountRowFlex>}
      />
    </React.Fragment>
  );
}

const mapStateToDemoAccLimit: MapStateToProps<*, *, *> = (
  state: ReduxState
) => ({
  demoAccLimit: (state.apiConfigs && state.apiConfigs.demoAccLimit) || 1000,
});

const DemoRefill = connect(mapStateToDemoAccLimit)(
  ({ demoAccLimit }: { demoAccLimit: number }) => <Money>{demoAccLimit}</Money>
);

const DemoAccountTabContent = ({ onDepositClick, onClose }) => (
  <React.Fragment>
    <AccountRowFlex>
      <AccountInfo>
        <Gettext>
          A demo account is a special account with virtual money, which allows
          you to check capabilities of the platform. You can't withdraw money
          from a demo account, but you can practice trading without any risk as
          much as you want. When a demo balance is close to {<DemoRefill />} you
          will be able to top it up for free.
        </Gettext>
      </AccountInfo>
    </AccountRowFlex>

    <Media
      query="(max-width: 959px)"
      render={() => (
        <AccountRowFlex>
          <AccountButton
            type="refill"
            onClick={(e) => {
              gaReducer("CLICKBTN_TRADE_DEPOSIT_ACCOUNT", {
                buttonText: e.currentTarget.textContent,
                accountType: "demo",
              });
              onDepositClick();
              onClose(e);
            }}
          >
            <Gettext>Deposit</Gettext>
          </AccountButton>

          <AccountButton type="withdraw" to="/withdraw">
            <Gettext>Withdraw</Gettext>
          </AccountButton>
        </AccountRowFlex>
      )}
    />
  </React.Fragment>
);

const BonusWrapper = styled.div`
  margin-bottom: 16px;
  background-color: #000;
  white-space: normal;
  background-image: url(${bonusBg});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  position: relative;
  border-radius: 4px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);

  :last-of-type {
    margin-bottom: 0;
  }
`;

const BonusConteiner = styled.div`
  position: relative;
  padding: 16px 16px 20px;
`;

const BonusTitle = styled.div`
  color: #ffcc00;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 17px;
  display: flex;
  align-items: center;
  mark {
    background-color: unset;
    color: #ffcc00;
    margin-right: 5px;
  }
`;

const BonusTitleIcon = styled.div`
  margin-right: 10px;
  flex: 0 0 auto;
  color: #bebebe;
`;

const BonusText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #fff;
  margin-bottom: 20px;
  font-weight: 700;
  @media (max-width: 479px) {
    line-height: 1.4;
  }
`;

const BonusFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 479px) {
    flex-direction: column;
  }
`;

const BonusIcons = styled.div`
  @media (max-width: 479px) {
    margin-bottom: 18px;
  }
`;

const BonusButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const BonusButton = styled(RawLink)`
  border: 2px solid #ffffff;
  border-radius: 19px;
  font-size: 12px;
  line-height: 1;
  padding: 10px 46px;
  background-color: transparent;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: 0.3s;
  text-transform: uppercase;
  text-decoration: none;
  :hover:not([disabled]) {
    background-color: #ffffff;
    color: #3b9478;
  }
  :disabled {
    color: #707070;
    border-color: #707070;
  }
  @media (max-width: 479px) {
    margin-bottom: 20px;
    width: 100%;
  }
`;

const BonusIcon = styled.div`
  margin: 0 3px;
  display: inline-block;
  svg path {
    fill: #ffcc00;
  }
`;

const BonusSticker = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 42px;
  height: 42px;
`;

const Bonus = ({ compensationUpdated, compensationCreated }) => {
  const compensations = useCompensations([
    compensationUpdated,
    compensationCreated,
  ]);

  const amountLimit = useMemo(() => getAmountLimit(compensations), [
    compensations,
  ]);

  const memoizedHotIcon = useMemo(() => <HotIcon />, []);
  const memoizedBonusIcons = useMemo(
    () => (
      <BonusIcon>
        <ShieldIcon />
      </BonusIcon>
    ),
    []
  );
  const memoizedGiftIcon = useMemo(
    () => (
      <BonusTitleIcon>
        <GiftIcon />
      </BonusTitleIcon>
    ),
    []
  );

  if (
    compensations == null ||
    compensations.demo == null ||
    compensations.demo.count < 1
  ) {
    return null;
  }

  return (
    <BonusWrapper>
      <BonusSticker>{memoizedHotIcon}</BonusSticker>
      <BonusConteiner>
        <BonusTitle>
          {memoizedGiftIcon}
          <Gettext>Bonus</Gettext>
        </BonusTitle>
        <BonusText>
          {/* prettier-ignore */}
          <Gettext>
                Don't worry about losing real money! Now we give newbie traders,
                who made their first deposit, {
                    <Gettext>
                      5 risk-free trades that can be used for all deals up to {<Money>{amountLimit}</Money>}
                    </Gettext>
                }
              </Gettext>
        </BonusText>
        <BonusFooter>
          <BonusIcons>
            {memoizedBonusIcons}
            {memoizedBonusIcons}
            {memoizedBonusIcons}
            {memoizedBonusIcons}
            {memoizedBonusIcons}
          </BonusIcons>
          <BonusButtonContainer>
            <BonusButton to="/fillup">
              <Gettext>Use bonus</Gettext>
            </BonusButton>
          </BonusButtonContainer>
        </BonusFooter>
      </BonusConteiner>
    </BonusWrapper>
  );
};

const CongratulationWrapper = styled.div`
  margin-bottom: 8px;
  background-color: ${(props) => props.theme.toggleAccountLightBg};
  white-space: normal;
  position: relative;
  padding: 16px;

  :last-of-type {
    margin-bottom: 0;
  }
`;

const CongratulationTitle = styled.div`
  color: ${({ theme }) => (theme.mode === "light" ? "#3B9478" : "#FFCC00")};
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
`;

const CongratulationTitleIcon = styled.div`
  margin-right: 10px;
  flex: 0 0 auto;
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#616265")};
`;

const CongratulationText = styled.div`
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#FFFFFF")};
  margin-bottom: 16px;
  @media (max-width: 479px) {
    line-height: 1.4;
  }
`;

const CongratulationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CongratulationLink = styled(RawLink)`
  color: ${({ theme }) => (theme.mode === "light" ? "#0085EB" : "#FFCC00")};
  font-size: 12px;
  text-transform: lowercase;
  ${(props) =>
    props.color === "yellow" &&
    css`
      color: #ffcc00;
    `};
`;

const CongratulationButton = styled.button`
  background-color: #0085eb;
  color: #fff;
  font-size: 12px;
  padding: 11px 22px;
  border-radius: 19px;
  border: none;
  text-transform: uppercase;
  font-weight: 900;
  transition: 0.3s;
  cursor: pointer;
  :hover:not([disabled]) {
    background-color: #0468b5;
  }
  :disabled {
    opacity: 0.4;
  }
`;

const TournamentBlock = styled.div`
  margin-bottom: 16px;
  margin-right: -16px;
  margin-left: -16px;
  background-color: #000;
  background-image: url(${(props) => props.img});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  position: relative;

  ::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;
const TournamentContent = styled.div`
  text-align: center;
  padding: 16px;
  position: relative;
`;
const TournamentTitle = styled.div`
  color: #ffffff;
  margin-bottom: 6px;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
`;
const TournamentFund = styled.div`
  color: #ffcc00;
  margin-bottom: 6px;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
`;
const TournamentStartLabel = styled.div`
  color: #ffffff;
  font-size: 12px;
  line-height: 1.2;
  margin-bottom: 6px;
`;
const TournamentStartTime = styled.div`
  color: #ffffff;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
`;
const TournamentSticker = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 42px;
  height: 42px;
`;

const Congratulation = ({ type, tournament }) => {
  const memoizedGiftIcon = useMemo(
    () => (
      <CongratulationTitleIcon>
        <GiftIcon />
      </CongratulationTitleIcon>
    ),
    []
  );
  const memoizedInviteSticker = useMemo(
    () => <img src={InviteImg} alt="" />,
    []
  );

  if (tournament == null || tournament.pay_account !== "demo") {
    return null;
  }

  return (
    <CongratulationWrapper>
      <CongratulationTitle>
        {memoizedGiftIcon}
        <Gettext>Congratulations</Gettext>
      </CongratulationTitle>
      <CongratulationText>
        <Gettext>
          You have enough DEMO money to purchase a tournament invitation!
        </Gettext>
      </CongratulationText>
      {type === "2" && (
        <TournamentBlock img={"https://picsum.photos/500"}>
          <TournamentSticker>{memoizedInviteSticker}</TournamentSticker>
          <TournamentContent>
            <TournamentTitle>__title__</TournamentTitle>
            <TournamentFund>
              <Gettext>Prize fund</Gettext>: $25
            </TournamentFund>
            <TournamentStartLabel>
              <Gettext>The tournament will start in</Gettext>:
            </TournamentStartLabel>
            <TournamentStartTime>02 : 19 : 18</TournamentStartTime>
          </TournamentContent>
        </TournamentBlock>
      )}

      {type === "1" && (
        <CongratulationFooter>
          <CongratulationLink to="/news/doubleup" target="_blank">
            <Gettext>Details</Gettext>
          </CongratulationLink>
          <ModalConsumer>
            {({ showModal }) => (
              <CongratulationButton
                onClick={() => showModal(JoinPopup, tournament)}
              >
                <Gettext>Buy the invitation</Gettext>
              </CongratulationButton>
            )}
          </ModalConsumer>
        </CongratulationFooter>
      )}
      {type === "2" && (
        <CongratulationFooter>
          <CongratulationLink
            to="/news/doubleup"
            target="_blank"
            color="yellow"
          >
            <Gettext>Other tournaments</Gettext>
          </CongratulationLink>
          <CongratulationButton>
            <Gettext>
              Buy for {<Money>{tournament.participation_price}</Money>} demo
              money
            </Gettext>
          </CongratulationButton>
        </CongratulationFooter>
      )}
    </CongratulationWrapper>
  );
};

const AdviceWrapper = styled.div`
  margin-bottom: 8px;
  background-color: ${(props) => props.theme.toggleAccountLightBg};
  white-space: normal;
  position: relative;
  padding: 16px;

  :last-of-type {
    margin-bottom: 0;
  }
`;
const AdviceTitle = styled.div`
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#FFFFFF")};
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 17px;
  display: flex;
  align-items: center;
`;

const AdviceText = styled.div`
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#FFFFFF")};
  @media (max-width: 479px) {
    line-height: 1.4;
  }
`;

const AdviceLink = styled(NewsLink)`
  color: ${({ theme }) => (theme.mode === "light" ? "#0085EB" : "#FFCC00")};
  font-size: 12px;
  text-transform: lowercase;
`;

const AdviceTitleIcon = styled.div`
  margin-right: 10px;
  flex: 0 0 auto;
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#616265")};
`;

const Advice = ({ participationFee }) => {
  const memoizedAdviceIcon = useMemo(
    () => (
      <AdviceTitleIcon>
        <AdviceIcon />
      </AdviceTitleIcon>
    ),
    []
  );

  return (
    <AdviceWrapper>
      <AdviceTitle>
        {memoizedAdviceIcon} <Gettext>Advice</Gettext>
      </AdviceTitle>
      <AdviceText>
        {/*prettier-ignore*/}
        <Gettext>
          Don't know what to do with your DEMO money?</Gettext>{" "}
        <Gettext>
          Earn {<Money>{participationFee}</Money>} and get a paid tournament
          invitation!
        </Gettext>{" "}
        <AdviceLink to="/doubleup" target="_blank">
          <Gettext>Details</Gettext>
        </AdviceLink>
      </AdviceText>
    </AdviceWrapper>
  );
};

const mapStateToProps: MapStateToProps<*, *, *> = (state: ReduxState) => ({
  profileReady: state.dataRequirementsStatus.profile === "AVAILABLE",
  tournaments: state.tournaments,
  tradeMode: state.settings.mode,
});

export default withRouter(
  connect(mapStateToProps)((props) => (
    <ModalConsumer>
      {({ queue }) => (
        <Tournament.Consumer>
          {(api) => <ToggleAccount {...props} queue={queue} api={api} />}
        </Tournament.Consumer>
      )}
    </ModalConsumer>
  ))
);



// WEBPACK FOOTER //
// ./src/components/AccountType/ToggleAccount.js